import { useEffect, useRef, useState } from 'react'
import type { User, UserManager } from 'oidc-client-ts'
import { removeAuthCookies, setAuthCookies } from 'cookies/authCookies'
import { reportClientError } from 'services/errorReportingService'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { ACCOUNT_INIT_COMPLETE } from 'actions/actionConstants'
import { recoverOidcSession } from './recoverOidcSession'
import { clearAuthMethodMarker, getOidcSessionProvider } from './authMethod'

// The restore blocks the page behind an overlay, so it must not hang forever.
const RESTORE_TIMEOUT_MS = 15_000

export class OidcRestoreTimeoutError extends Error {
  constructor() {
    super('OIDC session restore timed out')
    this.name = 'OidcRestoreTimeoutError'
  }
}

function withRestoreTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new OidcRestoreTimeoutError()), RESTORE_TIMEOUT_MS)
  })

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId))
}

// Mirrors the access token into the auth cookie on every userLoaded, and
// restores the session when SSR rendered anonymous because it had expired. Both
// run on one long-lived UserManager so its silent-renew keeps the cookie fresh.
// Returns whether a restore is in flight, for the blocking overlay.
export default function useOidcSessionCoordinator(): boolean {
  const [isMounted, setIsMounted] = useState(false)
  const [isRestoringSession, setIsRestoringSession] = useState(false)
  const dispatch = useAppDispatch()
  // A ref so the effect doesn't re-run (and rebuild the UserManager) when login
  // state flips true after a restore.
  const loggedInRef = useRef(false)
  loggedInRef.current = useAppSelector(selectLoggedIn)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    // Reads storage, so only evaluate after mount.
    if (!isMounted) {
      return
    }

    const provider = getOidcSessionProvider()
    if (!provider) {
      return
    }

    let userManager: UserManager | undefined
    let disposed = false

    const syncCookie = (user: User) => {
      if (user.access_token) {
        setAuthCookies(user.access_token)
      }
    }

    const onSilentRenewError = (error: Error) => {
      reportClientError(error, { context: 'oidcSilentRenew' }).catch(() => undefined)
    }

    import('./config')
      .then(async({ createOidcUserManager }) => {
        if (disposed) {
          return
        }
        userManager = createOidcUserManager(provider)
        userManager.events.addUserLoaded(syncCookie)
        userManager.events.addSilentRenewError(onSilentRenewError)

        const user = await userManager.getUser()
        if (user) {
          syncCookie(user)
        }

        // SSR rendered anonymous but a session is present: the token expired in
        // the background.
        if (!loggedInRef.current) {
          setIsRestoringSession(true)
          try {
            await withRestoreTimeout(dispatch(recoverOidcSession(userManager, provider)))
          } catch(error) {
            reportClientError(error, { context: 'oidcSessionRecover' }).catch(() => undefined)

            // A hung request says nothing about whether the session is still good,
            // so leave it intact for the next load to retry — only tear down when
            // the refresh token was actually rejected. Either way stop blocking.
            if (!(error instanceof OidcRestoreTimeoutError)) {
              // Refresh token rejected: clear everything so the next load settles
              // logged-out rather than re-attempting a doomed restore forever.
              await userManager.removeUser().catch(() => undefined)
              clearAuthMethodMarker()
              removeAuthCookies()
            }
            dispatch({ type: ACCOUNT_INIT_COMPLETE })
          } finally {
            if (!disposed) {
              setIsRestoringSession(false)
            }
          }
        }
      })
      .catch((error) => {
        // initialiseUser defers init for the OIDC path, so settle it here or the
        // app hangs uninitialised.
        reportClientError(error, { context: 'oidcSessionCoordinator' }).catch(() => undefined)
        if (!disposed) {
          setIsRestoringSession(false)
        }
        dispatch({ type: ACCOUNT_INIT_COMPLETE })
      })

    return () => {
      disposed = true
      if (userManager) {
        userManager.events.removeUserLoaded(syncCookie)
        userManager.events.removeSilentRenewError(onSilentRenewError)
      }
    }
  }, [isMounted, dispatch])

  return isRestoringSession
}
